import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import { Entry } from "contentful";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import myaccount from "~/components/common/myaccount.module.scss";
import EditorialEntry from "~/components/editorials/editorial-entry";
import { useAuthContext } from "~/contexts/auth";
import { useConfiguration } from "~/contexts/configuration";
import useSubscriptions from "~/hooks/use-subscriptions";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/cross-country-utils";
import { isActiveEntry } from "~/utils/editorial-utils";
import evaUtils from "~/utils/eva-utils";

import BirthdayCard from "./birthday-card";
import DataLavelBar from "./data-level-bar";
import LevelBenefits from "./level-benefits";

type Props = {
  editorialComponents?: Entry<unknown>[];
};

export default function LoyaltyProgram(props: Props) {
  const t = useTranslations();
  const { user } = useAuthContext();
  const router = useRouter();
  const currentCountryID = crossCountryUtils.getCurrentCountryCode(router);
  const evaEndpointUrl = hooks.useGetState(state.core.evaEndpointUrlState);

  const evaServicePrefigureDiscounts = hooks.useCallService({
    service: Core.PrefigureDiscounts,
  });

  // understand if the loyalty program is active for this specific organizational unit
  const { hasLoyaltyProgram, userSubscriptions } = useSubscriptions();
  const { configuration } = useConfiguration();

  // get data about all the loyalty program tiers
  const { data: prefigureDiscountsResults } = useQuery({
    queryKey: ["evaServicePrefigureDiscounts", user],
    staleTime: Infinity,
    queryFn: async () => {
      const resp = await evaServicePrefigureDiscounts();
      return resp?.Results;
    },
    enabled: !!evaEndpointUrl && !!hasLoyaltyProgram,
  });

  const currentTierNumber = useMemo(() => {
    return user
      ? (evaUtils.getCustomFieldValueForResource(
          Constants.LOYALTY_PROGRAM_TIER_BACKEND_ID,
          user,
          configuration
        ) as number)
      : 1;
  }, [configuration, user]);

  const birthdayPromo = useMemo(
    () => prefigureDiscountsResults?.find((promo) => promo.BackendID?.includes("BIRTHDAY_COUPON")),
    [prefigureDiscountsResults]
  );

  // actual level 1 of the loyalty program
  const welcomeGift = useMemo(
    () => prefigureDiscountsResults?.find((promo) => promo.BackendID?.includes("WELCOME_GIFT")),
    [prefigureDiscountsResults]
  );

  const loyaltyLevels = useMemo(() => {
    return prefigureDiscountsResults
      ?.filter((discount) => discount?.BackendID?.includes("TIER_"))
      .sort(
        (a, b) =>
          Number(a.BackendID?.split("TIER_")[1].split("_")[0]) - Number(b.BackendID.split("TIER_")[1].split("_")[0])
      );
  }, [prefigureDiscountsResults]);

  // const nextLevels = useMemo(() => {
  //   return loyaltyLevels?.filter((tier) => Number(tier.BackendID.split("_")[2]) > currentTierNumber);
  // }, [currentTierNumber, loyaltyLevels]);

  const currentLevels = useMemo(() => {
    if (loyaltyLevels && loyaltyLevels.length > 0) {
      if (welcomeGift && currentTierNumber == 1) {
        return [welcomeGift];
      } else {
        return loyaltyLevels.filter(
          (tier) => Number(tier.BackendID?.split("TIER_")[1].split("_")[0]) == currentTierNumber
        );
      }
    }
    return;
  }, [currentTierNumber, loyaltyLevels, welcomeGift]);

  // const prevLevels = useMemo(() => {
  //   let levels = loyaltyLevels?.filter((tier) => Number(tier.BackendID.split("_")[2]) < currentTierNumber);
  //   if (currentTierNumber > 1 && welcomeGift) {
  //     levels = levels?.concat([welcomeGift!]);
  //   }
  //   return levels;
  // }, [currentTierNumber, loyaltyLevels, welcomeGift]);

  if (!user) {
    return <></>;
  }

  return (
    <>
      {hasLoyaltyProgram ? (
        <>
          {userSubscriptions ? (
            <div className={myaccount.dashboardGrid}>
              <DataLavelBar />
              {/* TODO we should show the birthday gift when requrementsMet are true on the promo */}
              {birthdayPromo?.AvailableUserCoupons && birthdayPromo.AvailableUserCoupons.length > 0 && (
                <BirthdayCard promo={birthdayPromo} />
              )}
              {/* TODO: Hidden as per 2023/09/06 agreement. Since we don't have any information about the EVA discount associated to the current tier, and thus to the missing points required to go to the next level */}
              {loyaltyLevels && loyaltyLevels.length > 0 && (
                <>
                  {/* TODO: removed GWP. improve the experience or change the call
                  {!!currentLevels &&
                    currentLevels?.map((promo, i) => (
                      <LevelBenefits key={promo.BackendID} data={promo} currentLevel={i == 0} />
                    ))} */}
                  {/* according to last decisions - remove previous and next levels */}
                  {/* {nextLevels &&
                    nextLevels.length > 0 &&
                    nextLevels.map((discount) => <LevelBenefits data={discount} key={discount.BackendID} />)}
                  {prevLevels && prevLevels.length > 0 && (
                    <>
                      <div className={myaccount.separator}></div>
                      {prevLevels.map((discount) => (
                        <LevelBenefits data={discount} key={discount.BackendID} isPast />
                      ))}
                    </>
                  )} */}
                </>
              )}
              <div className={myaccount.separator} />
              <div className={myaccount.editorialContainer}>
                {props.editorialComponents?.filter(isActiveEntry).map((entry) => (
                  <EditorialEntry key={entry.sys.id} entry={entry} />
                ))}
              </div>
            </div>
          ) : (
            <p>{t("generic.notSubscribedToLoyalty")}</p>
          )}
        </>
      ) : (
        <p>{t("generic.noLoyaltyProgramAvailable")}</p>
      )}
    </>
  );
}
